import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { defaultDigiApp } from '@Utils/commonUtils';
import { isAfterOrEqual } from '@Utils/versionUtils';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { STORAGE_TYPE, getFromStorage } from '@Service/storageService';
import { getErrorMessageByVersion } from '@Service/versionService';
import { NOTIFICATION_HANDLER, DOCUMENT_IMPORT, NAVIGATION, MIX_PANEL } from '@Constants/types';
import { stopAnimation, abortAnimation, resetAnimation } from '@Actions/documentActions';
import { AuthorizePage } from '@Components/Authorize';
import { clearSubmissionIDs } from '@Utils/documentUtils';
import { getDigApps, startSLEProcess, getScoreCard } from '@Actions/scoreCardActions';
import { isAuthorized } from '@Service/authentication';
import Permission from '@Constants/permissions';
import getClauseLibrary from '@Actions/clauseActions';
import ScoreCardList from './components/ScoreCardList';
import { DocumentStatus } from '@Constants/status';
import AnalyzeDocument from '@Components/AnalyzeDocument';
import mixpanelEvents from '@Mixpanel';
import { usePermissions } from '@Context';

const permissions = [Permission.CLAUSE_READ];

const ScoreCardContainer = () => {
    const dispatch = useDispatch();
    const { user, version } = useSelector(state => state.auth);
    const documentStatus = useSelector(state => state.document.documentStatus);
    const digAppsFilteredArray = useSelector(state => state.scoreCard.digAppsFilteredArray);
    const {
        scorecard,
        enabled,
        isImporting,
        malConfigured,
        scorecardStatus,
        failedRequestsCount,
        scorecardPollingEnabled,
        failedMessage: { digApps: digAppsFailedMsg, scorecards: scorecardsFailedMsg }
    } = useSelector(state => state.scoreCard);
    const clauseLibraryLoadedState = useSelector(state => state.clause.clauseLibraryLoadedState);

    const { haveAccessToScorecardsPage } = usePermissions();

    const { pullingScorecards } = isImporting;

    const sealVersion = version;
    const digiAppName = getFromStorage('digAppName', STORAGE_TYPE.PROPERTY_BAG);

    const [selectedDigApp, setSelectedDigApp] = useState(
        getFromStorage('digAppId', STORAGE_TYPE.PROPERTY_BAG)
    );

    useEffect(() => {
        if (!enabled) dispatch(getDigApps(version));
        if (malConfigured) {
            addLog(
                LOG_TYPE.ERROR,
                'ScoreCardContainer useEffect[]',
                'Scorecard malconfiguration.  Analyzer is configured as both Enabled and with a schedule enabled'
            );
            dispatch({
                type: NOTIFICATION_HANDLER.WARNING,
                payload: 'Scorecard configuration issue. Contact your administrator'
            });
        }

        dispatch({ type: NAVIGATION.PAGE_LOADED });

        let loadExistingScorecard = true;
        const sleProcessStarted = getFromStorage('sleProcessStarted', STORAGE_TYPE.PROPERTY_BAG);
        if (!scorecard && !sleProcessStarted) {
            loadExistingScorecard = false;
        }

        mixpanelEvents.calculateScorecardRenderTime();

        const mixpanel = {
            loadExistingScorecard
        };
        dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_SCORECARD,
            payload: mixpanel
        });
        mixpanelEvents.resultsdisplayed();
    }, []);

    useEffect(
        () => {
            if (enabled && documentStatus === DocumentStatus.PROCESSED) {
                const sleProcessStarted = getFromStorage(
                    'sleProcessStarted',
                    STORAGE_TYPE.PROPERTY_BAG
                );
                if (isAfterOrEqual('7.5.0', version) && !scorecard && !sleProcessStarted) {
                    const digAppId = getFromStorage('digAppId', STORAGE_TYPE.PROPERTY_BAG);
                    const digAppName = getFromStorage('digAppName', STORAGE_TYPE.PROPERTY_BAG);
                    const documentId = getFromStorage(
                        'sealAtnDocumentId',
                        STORAGE_TYPE.PROPERTY_BAG
                    );

                    if (digAppId && digAppName && documentId) {
                        dispatch(startSLEProcess(version, digAppId, digAppName, documentId));
                    }
                }
            }
        },
        [documentStatus]
    );

    useEffect(
        () => {
            if (scorecard && scorecard.data && scorecard.data.riskMap) {
                addLog(
                    LOG_TYPE.INFO,
                    'ScoreCardContainer useEffect[scorecard]',
                    'Scorecard and riskMaps loaded successfully'
                );
            } else if (!scorecard) {
                addLog(
                    LOG_TYPE.INFO,
                    'ScoreCardContainer useEffect[scorecard]',
                    'Scorecard not loaded'
                );
            } else if (!sealVersion) {
                addLog(
                    LOG_TYPE.ERROR,
                    'ScoreCardContainer useEffect[scorecard]',
                    'Seal Version number UNDEFINED!  Try mocking the version number'
                );

                // only in development will the version number be undefined, so lets flag
                // this error as a warning.  An ordinary end-user will not see this message
                dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload:
                        'We have scorecard data, but the RiskMap is missing!  Have you mocked the version number to 7.2.1 or 7.3.x?'
                });
            } else {
                addLog(
                    LOG_TYPE.INFO,
                    'ScoreCardContainer useEffect[scorecard]',
                    `Seal Version = ${sealVersion}`
                );
                addLog(
                    LOG_TYPE.ERROR,
                    'ScoreCardContainer useEffect[scorecard]',
                    'RiskMap MISSING.  Scorecard data is present. If version is missing (i.e. un-mocked) then this error will occur.'
                );
                dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload: 'RiskMap MISSING.  Scorecard data is present'
                });
            }
        },
        [scorecard]
    );

    const getDigAppName = () => {
        let digApp = '';
        if (digAppsFilteredArray.length) {
            if (isAfterOrEqual('7.5.0', version)) {
                // get the digAppName from the document's properties from the previous submission (if any)
                const digAppIdPreviouslySubmittedOn = getFromStorage(
                    'digAppId',
                    STORAGE_TYPE.PROPERTY_BAG
                );
                if (digAppIdPreviouslySubmittedOn) {
                    digApp = digAppIdPreviouslySubmittedOn;
                }
            } else if (isAfterOrEqual('7.2.1', version)) {
                digApp = defaultDigiApp(digAppsFilteredArray);
            }
        }
        return digApp;
    };

    useEffect(
        () => {
            const docStatus = getFromStorage('sealAtnDocumentStatus', STORAGE_TYPE.PROPERTY_BAG);
            const documentId = getFromStorage('sealAtnDocumentId', STORAGE_TYPE.PROPERTY_BAG);
            const sealUrlSubmittedOn = getFromStorage('sealUrl', STORAGE_TYPE.PROPERTY_BAG);
            const selectedSystemUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);

            if (
                docStatus === DocumentStatus.PROCESSED ||
                documentStatus === DocumentStatus.PROCESSED
            ) {
                if (!scorecard) {
                    if (!documentId) {
                        abortAnimation();
                        clearSubmissionIDs();

                        dispatch({
                            type: DOCUMENT_IMPORT.IMPORT_ERROR
                        });

                        addLog(
                            LOG_TYPE.ERROR,
                            'ScoreCardContainer useEffect[documentStatus, selectedDigApp]',
                            'DocumentId is null'
                        );

                        dispatch({
                            type: NOTIFICATION_HANDLER.WARNING,
                            payload: 'Submission failed. Please retry'
                        });
                    }

                    const digAppNameOrId = getDigAppName();
                    if (!selectedDigApp) {
                        setSelectedDigApp(digAppNameOrId);
                    }

                    const availableDigAppsCount = digAppsFilteredArray.length;
                    if (
                        digAppNameOrId &&
                        sealUrlSubmittedOn === selectedSystemUrl &&
                        availableDigAppsCount > 0
                    ) {
                        if (
                            isAuthorized(user, permissions) &&
                            clauseLibraryLoadedState !== 'Loaded'
                        )
                            dispatch(getClauseLibrary());
                        dispatch(
                            getScoreCard(
                                digAppNameOrId,
                                version,
                                documentId,
                                sealVersion,
                                scorecardPollingEnabled
                            )
                        );
                    }
                }
            }
        },
        [documentStatus, selectedDigApp]
    );

    // Fires when scorecard pulling is required
    useEffect(
        () => {
            const documentId = getFromStorage('sealAtnDocumentId', STORAGE_TYPE.PROPERTY_BAG);
            const sealUrlSubmittedOn = getFromStorage('sealUrl', STORAGE_TYPE.PROPERTY_BAG);
            const selectedSystemUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);

            addLog(
                LOG_TYPE.INFO,
                'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                `documentId - ${documentId}`
            );

            addLog(
                LOG_TYPE.INFO,
                'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                `sealUrlSubmittedOn - ${sealUrlSubmittedOn}`
            );

            addLog(
                LOG_TYPE.INFO,
                'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                `selectedSystemUrl - ${selectedSystemUrl}`
            );

            addLog(
                LOG_TYPE.INFO,
                'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                `pullingScorecards - ${pullingScorecards}`
            );

            addLog(
                LOG_TYPE.INFO,
                'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                `scorecardPollingEnabled - ${scorecardPollingEnabled}`
            );

            if (documentStatus !== DocumentStatus.NOT_SUBMITTED) {
                if (!documentId) {
                    addLog(
                        LOG_TYPE.ERROR,
                        'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                        'DocumentId is null'
                    );
                }
                const digAppNameOrId = getDigAppName();
                if (!selectedDigApp) {
                    setSelectedDigApp(digAppNameOrId);
                }

                addLog(
                    LOG_TYPE.INFO,
                    'ScoreCardContainer useEffect[selectedDigApp, pullingScorecards]',
                    `digAppNameOrId - ${digAppNameOrId}`
                );

                if (
                    pullingScorecards &&
                    scorecardPollingEnabled &&
                    failedRequestsCount <= 100 &&
                    sealUrlSubmittedOn === selectedSystemUrl
                )
                    setTimeout(() => {
                        if (
                            isAuthorized(user, permissions) &&
                            clauseLibraryLoadedState !== 'Loaded'
                        )
                            dispatch(getClauseLibrary());
                        dispatch(
                            getScoreCard(
                                digAppNameOrId,
                                version,
                                documentId,
                                sealVersion,
                                scorecardPollingEnabled
                            )
                        );
                    }, 1000);
            }
        },
        [pullingScorecards]
    );

    useEffect(
        () => {
            let scorecardsFailedMessage = '';
            if (scorecardsFailedMsg) {
                if (scorecardsFailedMsg === 'ScorecardAnalyzer Name must be provided') {
                    scorecardsFailedMessage =
                        'There are no digital apps configured for this system';
                } else {
                    scorecardsFailedMessage =
                        'There was a problem starting the Scorecard build process, please try to submit the document again. Contact your system administrator if this issue persists.';
                }
            }

            // Wrong digApps response
            if (scorecardsFailedMsg) {
                dispatch({
                    type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                    payload: scorecardsFailedMessage
                });
                abortAnimation();
            }
        },
        [scorecardsFailedMsg]
    );

    useEffect(
        () => {
            // Wrong digApps response
            if (digAppsFailedMsg) {
                dispatch({
                    type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                    payload: digAppsFailedMsg
                });
                abortAnimation();
            }
        },
        [digAppsFailedMsg]
    );

    useEffect(
        () => {
            let technicalDetail = '';
            let digAppStatus = '';

            if (digAppsFilteredArray && digAppsFilteredArray.length === 0) {
                technicalDetail = 'There are no digital apps configured for this system';
            }

            if (digAppsFilteredArray && digAppsFilteredArray.length > 0) {
                digAppsFilteredArray.forEach(digApp => {
                    if (digApp.name === selectedDigApp) {
                        digAppStatus = digApp.status;
                    }
                });
            }

            // digApps not enabled
            if (!enabled && isImporting.digApps === false) {
                if (digAppStatus && digAppStatus.length > 0) {
                    switch (digAppStatus) {
                        case getErrorMessageByVersion('NO_SCORECARD_RULES', version):
                            technicalDetail = 'The scorecard rules are missing';
                            break;
                        case getErrorMessageByVersion('NO_ACTIVE_SCORECARD_RULES', version):
                            technicalDetail = 'There are no active scorecard rules';
                            break;
                        case getErrorMessageByVersion('UNABLE_TO_CONNECT_TO_SERVICE', version):
                            technicalDetail = 'Unable to connect to scorecard service';
                            break;
                        default:
                            technicalDetail = '';
                            break;
                    }
                }

                if (technicalDetail.length > 0) {
                    if (
                        technicalDetail === 'There are no digital apps configured for this system'
                    ) {
                        dispatch({
                            type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                            payload: 'There are no digital apps configured for this system'
                        });
                        abortAnimation();
                    }

                    const msg = `There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.  Technical details: ${technicalDetail}`;
                    dispatch({
                        type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                        payload: msg
                    });
                    abortAnimation();
                }

                dispatch({
                    type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                    payload:
                        'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator'
                });
                abortAnimation();
            }
        },
        [digAppsFilteredArray]
    );

    return (
        <AuthorizePage havePermission={haveAccessToScorecardsPage}>
            <AnalyzeDocument
                pageType="scorecard"
                documentStatus={documentStatus}
                scorecardStatus={scorecardStatus}
                data={scorecard}
                statues={['Initializing', 'Analyzing document', 'Generating scorecard']}
                stopAnimation={stopAnimation}
                resetAnimation={resetAnimation}
            >
                <ScoreCardList digAppName={digiAppName} />
            </AnalyzeDocument>
        </AuthorizePage>
    );
};

export default React.memo(ScoreCardContainer);
